import {Component, TemplateRef, ViewChild, OnInit, AfterViewInit, ChangeDetectionStrategy} from "@angular/core";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {PerFormaAPIService, TotpKeyResponse, TotpResponse} from "src/app/services/pfapi.service";
import {lastValueFrom} from "rxjs";
import {AuthService} from "src/app/services/auth.service";
import {PerformaModulesService} from "src/app/services/performa-modules.service";
import {NzNotificationService} from "ng-zorro-antd/notification";
import Utils from "src/utils";
import {Router} from "@angular/router";

@Component({
    selector: "app-two-factor",
    templateUrl: "./two-factor.component.html",
    styleUrls: ["./two-factor.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TwoFactorComponent implements OnInit, AfterViewInit {
    enrolled = false;
    loading = false;
    codeDto: TotpKeyResponse | null = null;
    code = "";
    codeError = "";
    confirmCode = "";
    force2fa = false;

    @ViewChild("qrTmpl") qrTmpl!: TemplateRef<any>;

    constructor(
        public readonly modal: NgbModal,
        private readonly api: PerFormaAPIService,
        private authService: AuthService,
        private modules: PerformaModulesService,
        private notify: NzNotificationService,
        private router: Router
    ) {}

    async ngOnInit() {
        this.enrolled = this.authService.twoFactorEnabled;
        this.force2fa = this.modules.features.some((x: string) => x.toLowerCase() === "force2fa");
    }

    async ngAfterViewInit() {
        if (this.force2fa && !this.enrolled) {
            this.showQr(this.qrTmpl);
        }
    }

    showDisable = async (tmpl: TemplateRef<any>) => {
        this.codeError = "";
        this.loading = true;
        this.code = "";
        this.confirmCode = "";
        this.modal.open(tmpl, {animation: false, backdropClass: "backdrop"});
    };

    showQr = async (tmpl: TemplateRef<any>) => {
        this.codeError = "";
        try {
            this.loading = true;

            this.codeDto = await lastValueFrom(this.api.postApiProfile2faKey());
            this.code = "";
            this.confirmCode = "";
            this.modal.open(tmpl, {
                animation: false,
                backdropClass: "backdrop",
                keyboard: !this.force2fa || this.enrolled,
                beforeDismiss: () => {
                    // if 2FA is globally forced, don't let them dismiss the modal
                    if (this.force2fa && !this.enrolled) return false;

                    return true;
                }
            });
        } catch (error: any) {
            console.log(error);
            this.codeError = error.error;
        } finally {
            this.loading = false;
        }
    };

    showReset = async (tmpl: TemplateRef<any>) => {
        this.codeError = "";
        try {
            this.loading = true;

            this.modal.open(tmpl, {
                animation: false,
                backdropClass: "backdrop",
                keyboard: true
            });
        } catch (error: any) {
            console.log(error);
            this.codeError = error.error;
        } finally {
            this.loading = false;
        }
    };

    disable = async () => {
        this.codeError = "";
        if (!this.code || !this.confirmCode || this.code !== this.confirmCode) {
            this.codeError = "Code mismatch";
            return;
        }
        try {
            const res = await this.api.postApiProfile2faToggle({Code: this.code, Enable: false}).toPromise();
            if (!res?.Success) {
                this.codeError = "Code mismatch";
                return;
            }

            this.enrolled = false;
            this.authService.twoFactorEnabled = false;

            this.modal.dismissAll();
        } catch (error: any) {
            this.codeError = error.error;
        }
    };

    reset = async () => {
        this.codeError = "";

        this.loading = true;

        try {
            const res: TotpResponse = await lastValueFrom(this.api.postApiProfile2faValidate({Code: this.code}));

            if (!res?.Success) {
                this.codeError = "Code mismatch";
                return;
            }

            await lastValueFrom(this.api.postApiProfile2faReset())
                .then(() => {
                    this.modal.dismissAll();

                    this.enrolled = false;
                    this.authService.twoFactorEnabled = false;

                    this.reloadProfile();
                })
                .catch(e => {
                    console.log(e);
                    this.codeError = "Something went wrong.";
                })
                .finally(() => {
                    this.loading = false;
                });
        } catch (error: any) {
            this.codeError = error.error;
        } finally {
            this.loading = false;
        }
    };

    finish = async () => {
        this.codeError = "";
        if (!this.code || !this.confirmCode || this.code !== this.confirmCode) {
            this.codeError = "Code mismatch";
            return;
        }
        try {
            const res: TotpResponse = await lastValueFrom(
                this.api.postApiProfile2faToggle({Code: this.code, Enable: true})
            );

            if (!res?.Success) {
                this.codeError = "Code mismatch";
                return;
            }
        } catch (error: any) {
            console.log(error);
            this.codeError = error.error;
            return;
        }

        this.enrolled = true;
        this.authService.twoFactorEnabled = true;

        this.modal.dismissAll();

        if (this.force2fa) this.reloadProfile();
    };

    copyToClipboard = (text: string | null) => {
        if (text) {
            Utils.CopyText(text);
            this.notify.success("", "Copied to clipboard", {nzPlacement: "bottom"});
        }
    };

    logout = async () => {
        // clear the SPA session and return to login (there is no server-side /account/logout route)
        localStorage.removeItem("pf_token");
        localStorage.removeItem("pf_refresh");
        this.router.navigateByUrl("/account/login");
    };

    /*
        This tricks the profile component's ngOnInit to run even though we're on the same 
        page. If we are on "/profile" then we route to "/profile/", and vice-versa.
    */
    reloadProfile = () => {
        this.router.navigateByUrl(`/profile${this.router.url.endsWith("/") ? "" : "/"}`);
    };
}
